// --- React & Redux
import React, { useEffect, useRef, useState } from "react";
import { useAsyncEffect } from "tap/utils/hooks/useAsyncEffect";
import { useLoading } from "tap/utils/hooks/useLoading";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
// --- External UI Libraries
import { Col, Row } from "antd";
import Button from "@dtci/spellbook/Button";
import Toggle from "@dtci/spellbook/Toggle";
import { MultiSelect } from "@dtci/spellbook/MultiSelect";
// --- Components
import { InputLabel } from "../../../../components/InputLabel";
import { ClearableFilterInput } from "tap/components/ClearableFilterInput";
// --- Actions & Hooks
import { useTPRExceptionsData } from "../../hooks/useTPRExceptionsData";
import { useTPRExceptionsActions } from "../../../../store/tprExceptions/useTPRExceptions";
// --- Utilities
import { tapApi } from "tap/api.js";
import {
  parseSearchParams,
  updateSearchParams,
} from "../../../../utils/urlParams";
import { validateSpecialCharacters } from "../../../../utils/validateSpecialCharacters";
import { getItemRange } from "../../../../utils/pagination";
import { buildTprExceptionsPageUpdate } from "../../utils/paginationUtils";
import {
  beginTprExceptionsFetch,
  isLatestTprExceptionsFetch,
} from "../../hooks/useTPRExceptionsData";
import {
  buildFilterFromParams,
  cleanTPRExceptionsFilter,
} from "./utils/tprExceptionsSearchUtils";
// --- Styles
import "./styles.scss";

export function TPRExceptionsSearchForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const { setLoadingTrue, setLoadingFalse } = useLoading();

  const {
    fetchTPRExceptions,
    loadUserOptions,
    updateTPRExceptions,
    tprExceptionsRange,
    tprExceptionsSort,
    filterForm,
    itemsPerPage,
  } = useTPRExceptionsData();

  const { updateTPRExceptionsSearchForm, clearTPRExceptions } =
    useTPRExceptionsActions();

  const {
    userOptions,
    userOptionsRevision,
    exceptionStatusOptions,
    exceptionTypeOptions,
    territoryOptions,
    mediaOptions,
    totalItems,
  } = useSelector((s) => s.tprExceptions);

  const [validationErrors, setValidationErrors] = useState({
    releaseId: undefined,
    releaseTitle: undefined,
    catalogName: undefined,
    licensee: undefined,
    catalogId: undefined,
    dealId: undefined,
  });
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Load user options on mount (independent of slower territory/media calls).
  useAsyncEffect(async ({ isMounted }) => {
    const users = await loadUserOptions();

    if (isMounted()) {
      updateTPRExceptions({
        userOptions: users ?? [],
        userOptionsRevision: Date.now(),
      });
    }
  }, []);

  // Load remaining dropdown options on mount.
  useAsyncEffect(async ({ isMounted }) => {
    try {
      const [territoryRes, mediaRes] = await Promise.all([
        tapApi.mediaAndTerritories("territory"),
        tapApi.mediaAndTerritories("media"),
      ]);

      if (isMounted()) {
        const territories = Array.isArray(territoryRes)
          ? territoryRes.map(({ label, value }) => ({ label, value }))
          : [];
        const medias = Array.isArray(mediaRes)
          ? mediaRes.map(({ label, value }) => ({ label, value }))
          : [];
        updateTPRExceptions({
          territoryOptions: territories,
          mediaOptions: medias,
        });
      }
    } catch (error) {
      console.error("Failed to load initial options:", error);
      if (isMounted()) {
        updateTPRExceptions({
          territoryOptions: [],
          mediaOptions: [],
        });
      }
    }
  }, []);

  // Sync URL → fetch when the query string changes (replace navigations may not change location.key).
  useEffect(() => {
    const params = parseSearchParams(location.search);

    // Build filter data from URL params using helper
    const filterData = buildFilterFromParams(
      params,
      userOptions,
      exceptionStatusOptions,
      exceptionTypeOptions,
      territoryOptions,
      mediaOptions,
    );

    let rangeOverride;
    if (filterData.pageSize) {
      rangeOverride = getItemRange(0, filterData.pageSize);
      updateTPRExceptions({
        itemsPerPage: filterData.pageSize,
        tprExceptionsRange: rangeOverride,
        currentPage: 0,
      });
    }

    const hasUrlParams = Object.keys(filterData).length > 0;

    // Update form state from URL params
    if (hasUrlParams) {
      updateTPRExceptionsSearchForm({ key: "filterForm", data: filterData });
    }

    // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
    // only carries partial params (e.g. pageSize).
    const filterForFetch =
      hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

    fetchData(filterForFetch, rangeOverride);
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // Re-parse URL params when async-loaded options arrive (users, territory, media).
  useEffect(() => {
    if (isInitialMount.current) return;

    const params = parseSearchParams(location.search);
    const hasAsyncMultiSelectParams =
      params.users || params.territories || params.medias;

    if (
      hasAsyncMultiSelectParams &&
      (userOptions.length > 0 ||
        territoryOptions.length > 0 ||
        mediaOptions.length > 0)
    ) {
      const filterData = {
        ...filterForm,
        ...buildFilterFromParams(
          params,
          userOptions,
          exceptionStatusOptions,
          exceptionTypeOptions,
          territoryOptions,
          mediaOptions,
        ),
      };

      updateTPRExceptionsSearchForm({ key: "filterForm", data: filterData });
      fetchData(filterData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOptions, territoryOptions, mediaOptions]);

  // Fetch data helper
  const fetchData = async (filterData, rangeOverride) => {
    setLoadingTrue();
    const fetchId = beginTprExceptionsFetch();
    const rawPageSize = filterData?.pageSize;
    const parsed =
      rawPageSize != null && rawPageSize !== "" ? Number(rawPageSize) : NaN;
    const effectiveItemsPerPage = Number.isFinite(parsed)
      ? parsed
      : itemsPerPage;
    const range =
      rangeOverride ?? getItemRange(0, effectiveItemsPerPage);
    const data = await fetchTPRExceptions(range, filterData);

    if (data && isLatestTprExceptionsFetch(fetchId)) {
      updateTPRExceptions(
        buildTprExceptionsPageUpdate(data, {
          itemsPerPage: effectiveItemsPerPage,
          currentPage: 0,
          range,
          previousTotalItems: totalItems,
          preserveTotal: false,
        }),
      );
    }
    setLoadingFalse();
  };

  // Reset filters - clear state and sync URL, let useEffect handle the fetch
  const handleResetFilter = () => {
    const preservedMineOnly = filterForm?.mineOnly;
    clearTPRExceptions();

    const urlParams = cleanTPRExceptionsFilter({ mineOnly: preservedMineOnly });
    updateSearchParams(location, navigate, urlParams);

    setValidationErrors({
      releaseId: undefined,
      releaseTitle: undefined,
      catalogName: undefined,
      licensee: undefined,
      catalogId: undefined,
      dealId: undefined,
    });
  };

  const handleInputChange = (field, value) => {
    let newValidationErrors = validationErrors;
    if (
      [
        "releaseId",
        "releaseTitle",
        "dealId",
        "catalogId",
        "licensee",
      ].includes(field)
    ) {
      const error = validateSpecialCharacters(value);
      newValidationErrors = { ...validationErrors, [field]: error };
      setValidationErrors(newValidationErrors);
    }

    const newFilterForm = { ...filterForm, [field]: value };

    updateTPRExceptionsSearchForm({
      key: "filterForm",
      data: { [field]: value },
    });

    // Debounced search
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const hasErrors = Object.values(newValidationErrors).some(
        (error) => error,
      );
      if (!hasErrors) {
        const filtered = cleanTPRExceptionsFilter(
          newFilterForm,
          getItemRange(0, itemsPerPage),
          tprExceptionsSort,
        );
        updateSearchParams(location, navigate, filtered);
      }
    }, 400);
  };

  const handleClearField = (field) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const newValidationErrors = { ...validationErrors, [field]: undefined };
    setValidationErrors(newValidationErrors);

    const newFilterForm = { ...filterForm, [field]: "" };
    updateTPRExceptionsSearchForm({
      key: "filterForm",
      data: { [field]: "" },
    });

    const resetRange = getItemRange(0, itemsPerPage);
    const filtered = cleanTPRExceptionsFilter(
      newFilterForm,
      resetRange,
      tprExceptionsSort,
    );
    updateSearchParams(location, navigate, filtered);
    fetchData(newFilterForm, resetRange);
  };

  return (
    <Row className="tpr-exceptions-filter-form">
      <InputLabel
        colProps={{ span: 24 }}
        label={filterForm?.mineOnly !== false ? "Show Mine" : "Show All"}
      >
        <Toggle
          className="tpr-exceptions-mine-toggle"
          labelStyle="yesNo"
          checked={filterForm?.mineOnly !== false}
          onChange={(e) => handleInputChange("mineOnly", e.target.checked)}
          width="auto"
          id="mineOnly"
          name="mineOnly"
        />
      </InputLabel>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="TPR Release ID">
          <ClearableFilterInput
            value={filterForm?.releaseId || ""}
            onChange={(e) => handleInputChange("releaseId", e.target.value)}
            onClear={() => handleClearField("releaseId")}
            maxLength={30}
            width="auto"
            id="releaseId"
            name="releaseId"
            placeholder="Enter"
            className={validationErrors.releaseId ? "input-error" : ""}
          />
          {validationErrors.releaseId && (
            <div className="validation-error">{validationErrors.releaseId}</div>
          )}
        </InputLabel>
      </Col>

      <Col>
        <InputLabel colProps={{ span: 24 }} label="TPR Release Title">
          <ClearableFilterInput
            value={filterForm?.releaseTitle || ""}
            onChange={(e) => handleInputChange("releaseTitle", e.target.value)}
            onClear={() => handleClearField("releaseTitle")}
            width="auto"
            maxLength={100}
            id="releaseTitle"
            name="releaseTitle"
            placeholder="Enter"
            className={validationErrors.releaseTitle ? "input-error" : ""}
          />
          {validationErrors.releaseTitle && (
            <div className="validation-error">
              {validationErrors.releaseTitle}
            </div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Deal ID">
          <ClearableFilterInput
            value={filterForm?.dealId || ""}
            onChange={(e) => handleInputChange("dealId", e.target.value)}
            onClear={() => handleClearField("dealId")}
            maxLength={30}
            width="auto"
            id="dealId"
            name="dealId"
            placeholder="Enter"
            className={validationErrors.dealId ? "input-error" : ""}
          />
          {validationErrors.dealId && (
            <div className="validation-error">{validationErrors.dealId}</div>
          )}
        </InputLabel>
      </Col>

      <Col>
        <InputLabel colProps={{ span: 24 }} label="Catalog ID">
          <ClearableFilterInput
            value={filterForm?.catalogId || ""}
            onChange={(e) => handleInputChange("catalogId", e.target.value)}
            onClear={() => handleClearField("catalogId")}
            maxLength={30}
            width="auto"
            id="catalogId"
            name="catalogId"
            placeholder="Enter"
            className={validationErrors.catalogId ? "input-error" : ""}
          />
          {validationErrors.catalogId && (
            <div className="validation-error">{validationErrors.catalogId}</div>
          )}
        </InputLabel>
      </Col>

      <Col>
        <InputLabel colProps={{ span: 24 }} label="Catalog Name">
          <ClearableFilterInput
            value={filterForm?.catalogName || ""}
            onChange={(e) => handleInputChange("catalogName", e.target.value)}
            onClear={() => handleClearField("catalogName")}
            width="auto"
            maxLength={100}
            id="catalogName"
            name="catalogName"
            placeholder="Enter"
            className={validationErrors.catalogName ? "input-error" : ""}
          />
          {validationErrors.catalogName && (
            <div className="validation-error">
              {validationErrors.catalogName}
            </div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Media">
          <MultiSelect
            onChange={(value) => handleInputChange("medias", value)}
            name="medias"
            options={mediaOptions}
            isSearchable
            isClearable
            value={filterForm.medias || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Territory">
          <MultiSelect
            onChange={(value) => handleInputChange("territories", value)}
            name="territories"
            options={territoryOptions}
            isSearchable
            isClearable
            value={filterForm.territories || ""}
          />
        </InputLabel>
      </Col>

      <Col>
        <InputLabel colProps={{ span: 24 }} label="Licensee">
          <ClearableFilterInput
            value={filterForm?.licensee || ""}
            onChange={(e) => handleInputChange("licensee", e.target.value)}
            onClear={() => handleClearField("licensee")}
            width="auto"
            maxLength={100}
            id="licensee"
            name="licensee"
            placeholder="Enter"
            className={validationErrors.licensee ? "input-error" : ""}
          />
          {validationErrors.licensee && (
            <div className="validation-error">{validationErrors.licensee}</div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Exception Status">
          <MultiSelect
            onChange={(value) => handleInputChange("exceptionStatus", value)}
            name="exceptionStatus"
            options={exceptionStatusOptions}
            isSearchable
            isClearable
            value={filterForm.exceptionStatus || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Exception Type">
          <MultiSelect
            onChange={(value) => handleInputChange("exceptionType", value)}
            name="exceptionType"
            options={exceptionTypeOptions}
            isSearchable
            isClearable
            value={filterForm.exceptionType || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Dismissed By">
          <MultiSelect
            key={`dismissed-by-${userOptionsRevision}`}
            onChange={(value) => handleInputChange("users", value)}
            name="users"
            options={userOptions}
            isSearchable
            isClearable
            value={filterForm.users || ""}
          />
        </InputLabel>
      </Col>
      <Col className="col-spacer-md" />
      <Col>
        <Button
          className="reset-button"
          sizeType="small"
          styleType="standard"
          onClick={handleResetFilter}
        >
          Reset
        </Button>
      </Col>
    </Row>
  );
}
